import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import axios from "axios";
import {
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Clock,
    Inbox,
    Loader2,
    MessageCircle,
    RefreshCw,
    TrendingUp,
    User,
} from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/chat`;

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-CA", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const STATUS_FILTERS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "closed", label: "Closed" },
    { key: "rate_limited", label: "Rate-Limited" },
    { key: "converted", label: "Converted" },
];

const Pill = ({ children, tone = "default" }) => (
    <span
        className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px]",
            tone === "cyan" && "bg-brand-cyan/15 text-brand-cyan",
            tone === "coral" && "bg-brand-coral/15 text-brand-coral",
            tone === "gray" && "bg-brand-gray/20 text-brand-dark/70",
            tone === "default" && "bg-brand-offwhite text-brand-dark",
        )}
    >
        {children}
    </span>
);

const ContextPills = ({ ctx }) => {
    if (!ctx) return <span className="text-brand-gray text-[11px]">—</span>;
    const items = [];
    (ctx.services_interested || []).forEach((s) => items.push({ key: `s-${s}`, label: s, tone: "cyan" }));
    if (ctx.city) items.push({ key: "city", label: ctx.city, tone: "default" });
    if (ctx.timeline) items.push({ key: "timeline", label: ctx.timeline, tone: "default" });
    if ((ctx.contact_intent_score || 0) >= 8)
        items.push({ key: "intent", label: `Intent ${ctx.contact_intent_score}/10`, tone: "coral" });
    if (items.length === 0) return <span className="text-brand-gray text-[11px]">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {items.map((it) => (
                <Pill key={it.key} tone={it.tone}>
                    {it.label}
                </Pill>
            ))}
        </div>
    );
};

const SessionDetail = ({ sessionId, headers }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/admin/sessions/${sessionId}`, {
                    headers,
                });
                if (active) setData(res.data);
            } catch {
                if (active) setData(null);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [sessionId, headers]);

    if (loading) {
        return (
            <div className="p-4">
                <Loader2 className="h-5 w-5 text-brand-cyan animate-spin" strokeWidth={2} />
            </div>
        );
    }
    if (!data) return <div className="p-4 text-sm text-brand-coral">Could not load session.</div>;

    return (
        <div className="bg-brand-offwhite border-t border-brand-dark/10 p-4 md:p-5 space-y-3" data-testid={`admin-chat-session-detail-${sessionId}`}>
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {(data.messages || []).map((m, i) => (
                    <div
                        key={i}
                        className={cn(
                            "flex",
                            m.role === "user" ? "justify-end" : "justify-start",
                        )}
                    >
                        <div
                            className={cn(
                                "max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap",
                                m.role === "user"
                                    ? "bg-brand-coral text-white"
                                    : "bg-white border-l-4 border-brand-cyan text-brand-dark",
                            )}
                        >
                            <p>{m.content}</p>
                            <p className="mt-1 text-[10px] uppercase tracking-wider opacity-60">
                                {m.role} · {formatDate(m.timestamp)}
                            </p>
                        </div>
                    </div>
                ))}
                {(data.messages || []).length === 0 && (
                    <p className="text-sm italic text-brand-gray">
                        No user messages yet — session started but conversation hasn&apos;t begun.
                    </p>
                )}
            </div>
            <div className="pt-3 border-t border-brand-dark/10 text-xs text-brand-dark/70 grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                    <span className="font-semibold text-brand-dark">User agent:</span>{" "}
                    <span className="line-clamp-1">{data.user_agent || "—"}</span>
                </div>
                <div>
                    <span className="font-semibold text-brand-dark">IP hash:</span>{" "}
                    {(data.visitor_ip_hash || "").slice(0, 12)}…
                </div>
                <div>
                    <span className="font-semibold text-brand-dark">Tokens:</span>{" "}
                    {data.total_tokens_used || 0}
                </div>
            </div>
        </div>
    );
};

const AdminChatPanel = ({ token }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all");
    const [expandedId, setExpandedId] = useState(null);

    const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

    const fetchSessions = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = {};
            if (filter === "converted") params.converted = true;
            else if (filter !== "all") params.status = filter;
            const res = await axios.get(`${API}/admin/sessions`, { headers, params });
            setSessions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Could not load chat sessions.");
        } finally {
            setLoading(false);
        }
    }, [headers, filter]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    return (
        <div data-testid="admin-chat-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-display text-3xl tracking-wide text-brand-dark leading-none">
                        Chat Sessions
                    </h2>
                    <p
                        className="mt-2 text-sm text-brand-dark/70"
                        data-testid="admin-chat-total"
                    >
                        <span className="font-semibold text-brand-dark">{sessions.length}</span>{" "}
                        session{sessions.length === 1 ? "" : "s"} — newest first.
                    </p>
                </div>
                <BrandButton
                    variant="outline"
                    tone="onLight"
                    size="md"
                    onClick={fetchSessions}
                    disabled={loading}
                    data-testid="admin-chat-refresh"
                >
                    {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                    ) : (
                        <RefreshCw className="h-4 w-4" strokeWidth={2.25} />
                    )}
                    Refresh
                </BrandButton>
            </div>

            {/* Filter chips */}
            <div
                className="flex flex-wrap items-center gap-2 mb-6"
                role="tablist"
                aria-label="Filter chat sessions"
                data-testid="admin-chat-filters"
            >
                {STATUS_FILTERS.map((f) => {
                    const active = filter === f.key;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFilter(f.key)}
                            role="tab"
                            aria-selected={active}
                            className={cn(
                                "inline-flex items-center px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors duration-200 border",
                                active
                                    ? "bg-brand-dark text-white border-brand-dark"
                                    : "bg-white text-brand-dark border-brand-dark/15 hover:border-brand-cyan hover:text-brand-cyan",
                            )}
                            data-testid={`admin-chat-filter-${f.key}`}
                        >
                            {f.label}
                        </button>
                    );
                })}
            </div>

            {error && (
                <div
                    className="mb-6 border-l-4 border-brand-coral bg-brand-coral/10 p-4 text-sm text-brand-dark"
                    data-testid="admin-chat-error"
                >
                    <p className="font-semibold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                        {error}
                    </p>
                </div>
            )}

            {!loading && sessions.length === 0 ? (
                <div
                    className="bg-white border-2 border-dashed border-brand-dark/15 p-16 text-center"
                    data-testid="admin-chat-empty"
                >
                    <Inbox className="h-10 w-10 mx-auto text-brand-gray mb-4" strokeWidth={1.5} />
                    <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-2">
                        No chat sessions yet.
                    </h3>
                    <p className="text-sm text-brand-dark/60">
                        Visitor chats will appear here as soon as someone opens the widget.
                    </p>
                </div>
            ) : (
                <div className="bg-white border border-brand-dark/10 overflow-x-auto" data-testid="admin-chat-table-wrapper">
                    <table className="w-full text-left">
                        <thead className="bg-brand-dark text-white text-[10px] uppercase tracking-wider font-semibold">
                            <tr>
                                <th className="px-3 py-3">Last activity</th>
                                <th className="px-3 py-3">Started</th>
                                <th className="px-3 py-3">Initial page</th>
                                <th className="px-3 py-3">Messages</th>
                                <th className="px-3 py-3">Tokens</th>
                                <th className="px-3 py-3">Status</th>
                                <th className="px-3 py-3">Context</th>
                                <th className="px-3 py-3">Converted</th>
                                <th className="px-3 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => {
                                const expanded = expandedId === s.id;
                                return (
                                    <Fragment key={s.id}>
                                        <tr
                                            className="border-b border-brand-dark/10 hover:bg-brand-offwhite transition-colors"
                                            data-testid={`admin-chat-row-${s.id}`}
                                        >
                                            <td className="px-3 py-3 text-xs text-brand-dark/80 whitespace-nowrap">
                                                <Clock className="inline h-3 w-3 mr-1 text-brand-gray" strokeWidth={2.25} />
                                                {formatDate(s.last_message_at)}
                                            </td>
                                            <td className="px-3 py-3 text-xs text-brand-dark/70 whitespace-nowrap">
                                                {formatDate(s.created_at)}
                                            </td>
                                            <td className="px-3 py-3 text-xs font-mono text-brand-dark/80 max-w-[180px] truncate">
                                                {s.initial_page || "/"}
                                            </td>
                                            <td className="px-3 py-3 text-xs whitespace-nowrap">
                                                <MessageCircle className="inline h-3 w-3 mr-1 text-brand-gray" strokeWidth={2.25} />
                                                {s.message_count}
                                            </td>
                                            <td className="px-3 py-3 text-xs text-brand-dark/70">
                                                {s.total_tokens_used.toLocaleString()}
                                            </td>
                                            <td className="px-3 py-3 text-xs whitespace-nowrap">
                                                {s.status === "active" ? (
                                                    <Pill tone="cyan">Active</Pill>
                                                ) : s.status === "rate_limited" ? (
                                                    <Pill tone="coral">Rate-Limited</Pill>
                                                ) : (
                                                    <Pill tone="gray">{s.status}</Pill>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-xs">
                                                <ContextPills ctx={s.extracted_context} />
                                            </td>
                                            <td className="px-3 py-3 text-xs">
                                                {s.converted_to_quote ? (
                                                    <span className="inline-flex items-center gap-1 text-brand-cyan font-semibold">
                                                        <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                                                        Yes
                                                    </span>
                                                ) : (
                                                    <span className="text-brand-gray">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setExpandedId(expanded ? null : s.id)
                                                    }
                                                    className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-brand-cyan hover:text-brand-dark transition-colors"
                                                    data-testid={`admin-chat-expand-${s.id}`}
                                                >
                                                    {expanded ? "Hide" : "View"}
                                                    {expanded ? (
                                                        <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                    ) : (
                                                        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr>
                                                <td colSpan={9} className="p-0">
                                                    <SessionDetail
                                                        sessionId={s.id}
                                                        headers={headers}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminChatPanel;
